/* Badger Sentinel — the scenario module `region-map.js` imports.
   NOT VENDORED. This path is the renderer's, not ours: `boot()` runs

       const mod = await import('./scenario.js?v=3');
       this.S = mod;

   so the module has to sit beside the renderer under this exact name. The
   renderer stays byte-identical to its source in the Claude Design project
   (M5-D7) and this file is a companion, the same way `mw-map.d.ts` is.

   It holds no scenario data and no logic. The model is built from `CellViewWire`
   by `src/console/model/scenario.ts` and pushed in by `console/maps/RegionMap.tsx`;
   everything below is plumbing between the two.

   ── Why the state lives on `window` ──────────────────────────────────────────
   The renderer imports this with a `?v=3` query and the React wrapper imports it
   without one. Vite keys modules by resolved id, query included, so those are two
   module instances of this file — and a `let` reassigned in one is invisible in
   the other. That failure is silent: the map would simply render an empty world
   forever.

   So the cell is created once on `window`, every instance exports the SAME array
   objects out of it, and adoption mutates those arrays in place. Any number of
   instances then see one model. `window.BS_SCENARIO` is the cell's model, which
   is also the global `region-map.js` reads directly in `fitSide()`. */

const cell = (window.__BS_SCENARIO_CELL ??= {
  model: null,
  REGIONS: [],
  UNITS: [],
  WX_GLYPH: {},
  ROUTERS: [],
  NETWORKS: [],
  ACCESS_MARK: {},
  ACCESS_SHORT: [],
  FOOTING: {},
});

/** The declared zones: `{id, name, sources}`. Read by every draw pass. */
export const REGIONS = cell.REGIONS;
/** Every asset and contact, in the renderer's own unit shape. */
export const UNITS = cell.UNITS;
/** Weather state → the mark drawn beside a region name. */
export const WX_GLYPH = cell.WX_GLYPH;

/** Adopt a model built by `model/scenario.ts`. Mutates in place — see the header. */
export function adopt(model) {
  cell.model = model;
  REGIONS.length = 0;
  REGIONS.push(...model.REGIONS);
  UNITS.length = 0;
  UNITS.push(...model.UNITS);
  for (const key of Object.keys(WX_GLYPH)) delete WX_GLYPH[key];
  Object.assign(WX_GLYPH, model.WX_GLYPH);
  ROUTERS.length = 0;
  ROUTERS.push(...model.ROUTERS);
  NETWORKS.length = 0;
  NETWORKS.push(...model.NETWORKS);
  for (const key of Object.keys(ACCESS_MARK)) delete ACCESS_MARK[key];
  Object.assign(ACCESS_MARK, model.ACCESS_MARK);
  ACCESS_SHORT.length = 0;
  ACCESS_SHORT.push(...model.ACCESS_SHORT);
  for (const key of Object.keys(FOOTING)) delete FOOTING[key];
  Object.assign(FOOTING, model.FOOTING);
  // `region-map.js` reads this global directly in `fitSide()` and never gets it
  // from `this.S`, so the two must be the same object or FIT TO FRIENDLY FORCES
  // silently does nothing.
  window.BS_SCENARIO = model;
}

/** Forget the model. The arrays stay the same objects; they are simply empty. */
export function release() {
  cell.model = null;
  REGIONS.length = 0;
  UNITS.length = 0;
  ROUTERS.length = 0;
  NETWORKS.length = 0;
  for (const key of Object.keys(ACCESS_MARK)) delete ACCESS_MARK[key];
  ACCESS_SHORT.length = 0;
  for (const key of Object.keys(FOOTING)) delete FOOTING[key];
  window.BS_SCENARIO = null;
}

// Every function below reads the cell rather than closing over a model, so an
// instance that was never adopted still answers with the live one.
export function unitById(id) {
  return cell.model ? cell.model.unitById(id) : undefined;
}

export function visible(unit) {
  return cell.model ? cell.model.visible(unit) : true;
}

export function isCyberNode(unit) {
  return cell.model ? cell.model.isCyberNode(unit) : false;
}

export function reachBands(unit) {
  return cell.model ? cell.model.reachBands(unit) : { cost: {}, count: 0, total: 0 };
}

export function weatherOf(region) {
  return cell.model ? cell.model.weatherOf(region) : [null, null];
}

/* Slots in one orbital band — `map.bandSlots`, which says HEO has 4 and every
   other band 24. Read by `orbital-map.js` to divide its rings, and the reason
   that file is not byte-identical to its source: see `model/orbital.ts`. The
   fallback is the renderer's own 24, so an unadopted instance draws what it
   always drew. */
export function slotsIn(band) {
  return cell.model ? cell.model.slotsIn(band) : 24;
}

/* ── The cyber board's half ───────────────────────────────────────────────────
   `cyber-map.js` reads fourteen more members off this module than the other
   three renderers do, and every one of them is `model/cyber.ts`'s. They are
   exported the same way everything above is — through the cell, so an instance
   that was never adopted still answers with the live model.

   **The three constant tables are live getters, not module constants.** The
   renderer reads `S.ACCESS_MARK[...]` and `Object.keys(S.FOOTING)` directly, so
   they have to be *bindings* that follow adoption rather than values captured at
   import — a `const` here would be `undefined` in the instance the renderer
   loaded under `?v=3` and its legend would throw on open.

   Every fallback below is the empty or unknown answer rather than a plausible
   one. An unadopted facade draws an empty world, which is what it is. */

/** The declared enclaves, one per segment: `{id, net, name, harden, status}`. */
export const ROUTERS = cell.ROUTERS ?? (cell.ROUTERS = []);
/** The two gateways, keyed `BLU` / `OPF` — the renderer's `net`. */
export const NETWORKS = cell.NETWORKS ?? (cell.NETWORKS = []);

export function hostsOf(routerId) {
  return cell.model ? cell.model.hostsOf(routerId) : [];
}

export function routerIdOf(unitId) {
  return cell.model ? cell.model.routerIdOf(unitId) : '';
}

export function isCyberTeam(unit) {
  return cell.model ? cell.model.isCyberTeam(unit) : false;
}

export function accessFor(unit) {
  return cell.model ? cell.model.accessFor(unit) : 'UNKNOWN';
}

export function access(id) {
  return cell.model ? cell.model.access(id) : 'UNKNOWN';
}

export function hardenOf(id) {
  return cell.model ? cell.model.hardenOf(id) : 'UNASSESSED';
}

export function accessRung(level) {
  return cell.model ? cell.model.accessRung(level) : 0;
}

export function footingFor(hardening) {
  return cell.model ? cell.model.footingFor(hardening) : { form: 'unknown' };
}

export function iconHref(cls) {
  return cell.model ? cell.model.iconHref(cls) : 'icons/console/' + cls + '.svg';
}

export function networkOf(routerId) {
  return cell.model ? cell.model.networkOf(routerId) : null;
}

/* Read by the renderer as plain data (`S.ACCESS_MARK[acc]`,
   `S.ACCESS_SHORT[rung]`, `Object.keys(S.FOOTING)`), so they follow the same
   rule as `REGIONS`, `UNITS` and `WX_GLYPH`: **one object out of the cell,
   mutated in place, never reassigned.**

   This was `let` bindings and a reassigning `adopt` for one revision, on the
   theory that ES module live bindings would carry the new value to every
   importer. They do not — a live binding is live within *one module instance*,
   and the renderer's `?v=3` import is a second instance whose `ACCESS_MARK` was
   still the empty object it was initialised to. The visible cost was exactly
   what an empty table produces and nothing louder: no access meter on any card,
   `undefined` where a segment header names a rung, and a legend whose ACCESS and
   POSTURE groups drew their headings with no rows under them. Nothing threw.

   `ACCESS_PIPS` is a plain `const` because it is a constant of the rules
   (`ACCESS_LEVELS.length - 1`) rather than anything the view decides. */
export const ACCESS_MARK = cell.ACCESS_MARK ?? (cell.ACCESS_MARK = {});
export const ACCESS_SHORT = cell.ACCESS_SHORT ?? (cell.ACCESS_SHORT = []);
export const FOOTING = cell.FOOTING ?? (cell.FOOTING = {});
export const ACCESS_PIPS = 3;
